
import { useCart } from '../contexts/CartContext';
import { toast } from 'react-hot-toast';

export function useCartActions() {
    const cart = useCart();
    
    // Agregar producto al carrito con validaciones
    const addToCart = (producto, cantidad = 1) => {
        try {
            // Normalizar el producto para el carrito (asegura imagen y campos claves)
            const normalized = {
                id: producto.id,
                nombre: producto.nombre || (producto.comercial && producto.comercial.nombre) || 'Producto',
                precio_venta: parseFloat(
                    producto.precio_venta != null ? producto.precio_venta : (producto.precio != null ? producto.precio : 0)
                ),
                imagen_principal: producto.imagen_principal || producto.foto_url || producto.imagen_portada || producto.imagen || null,
                stock_disponible: (
                    producto.stock_disponible != null ? producto.stock_disponible : (
                        producto.stock != null ? producto.stock : (producto.total_stock != null ? producto.total_stock : 0)
                    )
                )
            };
            
            // Validar stock disponible
            if (normalized.stock_disponible && cantidad > normalized.stock_disponible) {
                toast.error(`Solo hay ${normalized.stock_disponible} unidades disponibles`);
                return false;
            }
            
            // Verificar si ya existe en el carrito
            const existingItem = cart.items.find(item => item.id === normalized.id);
            const totalQuantity = existingItem ? existingItem.cantidad + cantidad : cantidad;
            
            if (normalized.stock_disponible && totalQuantity > normalized.stock_disponible) {
                toast.error(`No puedes agregar más de ${normalized.stock_disponible} unidades`);
                return false;
            }
            
            cart.addItem(normalized, cantidad);
            toast.success(`${normalized.nombre} agregado al carrito`);
            return true;
        } catch (error) {
            console.error('Error adding to cart:', error);
            toast.error('Error al agregar el producto al carrito');
            return false;
        }
    };
    
    // Remover producto del carrito
    const removeFromCart = (id, nombre) => {
        try {
            cart.removeItem(id);
            toast.success(`${nombre} removido del carrito`);
        } catch (error) {
            console.error('Error removing from cart:', error);
            toast.error('Error al remover el producto del carrito');
        }
    };
    
    // Actualizar cantidad con validaciones
    const updateCartQuantity = (id, cantidad, stock_disponible, nombre) => {
        try {
            if (cantidad <= 0) {
                removeFromCart(id, nombre);
                return;
            }
            
            if (stock_disponible && cantidad > stock_disponible) {
                toast.error(`Solo hay ${stock_disponible} unidades disponibles`);
                return;
            }
            
            cart.updateQuantity(id, cantidad);
        } catch (error) {
            console.error('Error updating quantity:', error);
            toast.error('Error al actualizar la cantidad');
        }
    };
    
    // Limpiar carrito con confirmación
    const clearCartWithConfirmation = () => {
        if (cart.items.length === 0) {
            toast.error('El carrito ya está vacío');
            return;
        }
        
        if (window.confirm('¿Estás seguro de que quieres vaciar el carrito?')) {
            cart.clearCart();
            toast.success('Carrito vaciado');
        }
    };
    
    // Verificar si un producto está en el carrito
    const isInCart = (productId) => {
        return cart.items.some(item => item.id === productId);
    };
    
    // Obtener cantidad de un producto en el carrito
    const getItemQuantity = (productId) => {
        const item = cart.items.find(item => item.id === productId);
        return item ? item.cantidad : 0;
    };
    
    // Calcular subtotal de un item
    const getItemSubtotal = (item) => {
        return item.precio * item.cantidad;
    };
    
    // Formatear precio
    const formatPrice = (price) => {
        return `S/. ${new Intl.NumberFormat('es-PE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(price)}`;
    };
    
    // Verificar si el carrito tiene productos
    const hasItems = cart.items.length > 0;
    
    // Obtener resumen del carrito
    const getCartSummary = () => {
        return {
            itemCount: cart.itemCount,
            total: cart.total,
            formattedTotal: formatPrice(cart.total),
            isEmpty: !hasItems
        };
    };
    
    return {
        // Estado del carrito
        ...cart,
        
        // Acciones
        addToCart,
        removeFromCart,
        updateCartQuantity,
        clearCartWithConfirmation,
        
        // Utilidades
        isInCart,
        getItemQuantity,
        getItemSubtotal,
        formatPrice,
        hasItems,
        getCartSummary
    };
}

export default useCartActions;
